import type { Conversation } from '@twilio/conversations';

import { useChatStore } from '@south-street-app/stores';

type ChatConversationAttributes = {
  type?: 'SUPPORT' | 'TRADE' | 'BROADCAST' | string;
  status?: 'pending' | 'resolved' | null;
  assignedTo?: string | null;
  endUserId?: string | null;
};

type ConversationPreview = {
  body: string;
  dateCreated: Date | null;
};

const SUPPORT_UNIQUE_NAME_PREFIX = 'SUPPORT:';
const TRADE_UNIQUE_NAME_PREFIX = 'TRADE:';
const BROADCAST_UPDATES_UNIQUE_NAME = 'BROADCAST:UPDATES';

const getConversationAttributes = (
  conversation: Conversation | null | undefined,
): ChatConversationAttributes => {
  if (!conversation) {
    return {};
  }

  return (conversation.attributes as ChatConversationAttributes | null) ?? {};
};

const isSupportConversation = (
  conversationOrUniqueName: Conversation | string | null | undefined,
) => {
  const uniqueName =
    typeof conversationOrUniqueName === 'string'
      ? conversationOrUniqueName
      : conversationOrUniqueName?.uniqueName;

  if (!uniqueName) {
    return false;
  }

  return (
    uniqueName.startsWith(SUPPORT_UNIQUE_NAME_PREFIX) ||
    getConversationAttributes(
      typeof conversationOrUniqueName === 'string'
        ? undefined
        : conversationOrUniqueName,
    ).type === 'SUPPORT'
  );
};

const isTradeConversation = (
  conversationOrUniqueName: Conversation | string | null | undefined,
) => {
  const uniqueName =
    typeof conversationOrUniqueName === 'string'
      ? conversationOrUniqueName
      : conversationOrUniqueName?.uniqueName;

  if (!uniqueName) {
    return false;
  }

  return (
    uniqueName.startsWith(TRADE_UNIQUE_NAME_PREFIX) ||
    getConversationAttributes(
      typeof conversationOrUniqueName === 'string'
        ? undefined
        : conversationOrUniqueName,
    ).type === 'TRADE'
  );
};

const isUpdatesConversation = (
  conversationOrUniqueName: Conversation | string | null | undefined,
) => {
  const uniqueName =
    typeof conversationOrUniqueName === 'string'
      ? conversationOrUniqueName
      : conversationOrUniqueName?.uniqueName;

  if (!uniqueName) {
    return false;
  }

  return (
    uniqueName === BROADCAST_UPDATES_UNIQUE_NAME ||
    getConversationAttributes(
      typeof conversationOrUniqueName === 'string'
        ? undefined
        : conversationOrUniqueName,
    ).type === 'BROADCAST'
  );
};

const isVisibleConversationMessage = (
  author: string | null,
  body: string | null,
) => {
  if (!author || !body?.trim()) {
    return false;
  }

  return author.startsWith('END_USER:') || author.startsWith('STAFF_USER:');
};

const sortArchivedConversations = (conversations: Conversation[]) =>
  [...conversations].sort(
    (left, right) =>
      (right.lastMessage?.dateCreated?.getTime() ?? 0) -
      (left.lastMessage?.dateCreated?.getTime() ?? 0),
  );

const getConversationPreview = async (
  conversation: Conversation | null | undefined,
) => {
  if (!conversation) {
    return null;
  }

  const paginator = await conversation.getMessages(10);

  const previewMessage = paginator.items.find((message) =>
    isVisibleConversationMessage(message.author, message.body),
  );

  if (!previewMessage?.body) {
    return null;
  }

  return {
    body: previewMessage.body,
    dateCreated: previewMessage.dateCreated ?? null,
  } satisfies ConversationPreview;
};

const refreshUnreadCounts = async () => {
  const {
    supportConversation,
    updatesConversation,
    setSupportUnreadCount,
    setUpdatesUnreadCount,
  } = useChatStore.getState();

  const [supportUnreadCount, updatesUnreadCount] = await Promise.all([
    supportConversation?.getUnreadMessagesCount(),
    updatesConversation?.getUnreadMessagesCount(),
  ]);

  setSupportUnreadCount(supportUnreadCount ?? 0);

  setUpdatesUnreadCount(updatesUnreadCount ?? 0);
};

const markConversationRead = async (
  conversation: Conversation | null | undefined,
) => {
  if (!conversation) {
    return;
  }

  await conversation.setAllMessagesRead();

  await refreshUnreadCounts();
};

export {
  BROADCAST_UPDATES_UNIQUE_NAME,
  SUPPORT_UNIQUE_NAME_PREFIX,
  TRADE_UNIQUE_NAME_PREFIX,
  getConversationAttributes,
  getConversationPreview,
  isSupportConversation,
  isTradeConversation,
  isUpdatesConversation,
  isVisibleConversationMessage,
  markConversationRead,
  refreshUnreadCounts,
  sortArchivedConversations,
};
export type { ChatConversationAttributes, ConversationPreview };
